import { Box, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  AxleOrgSyncOrgFragment,
  useAxleOrgSyncJobStatusesQuery,
  useAxleOrgSyncOrgsQuery,
  useCreateAxleOrgSyncJobMutation,
} from "../../../../types";
import { OrgsListSynced, OrgsListUnlinked } from "../../../../views/Staff/Orgs/Sync/Orgs/Axle";
import ListsColumn from "../../../../views/Staff/Orgs/Sync/shared/ListColumn";

const isInFlightStatus = (status: string | null | undefined): boolean => status === "pending" || status === "inProgress";

const hasActiveSync = (orgs: readonly { latestAxleOrgSyncJob?: { status: string } | null }[]): boolean =>
  orgs.some(org => isInFlightStatus(org.latestAxleOrgSyncJob?.status));

export const AxleOrgsSyncPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNutritionOrg, setSelectedNutritionOrg] = useState<AxleOrgSyncOrgFragment | null>(null);

  const { data, loading, refetch } = useAxleOrgSyncOrgsQuery();
  const [createAxleOrgSyncJob] = useCreateAxleOrgSyncJobMutation();

  const orgs = data?.orgs ?? [];
  const [pollStatuses, setPollStatuses] = useState(false);

  // Only enable from org data — never disable here. Stopping requires a status
  // poll result so we do not race a post-create refetch that still looks idle.
  useEffect(() => {
    if (hasActiveSync(orgs)) {
      setPollStatuses(true);
    }
  }, [orgs]);

  const { data: statusData } = useAxleOrgSyncJobStatusesQuery({
    skip: !pollStatuses,
    pollInterval: pollStatuses ? 5000 : 0,
  });

  const orgsWithFreshStatus = useMemo((): readonly AxleOrgSyncOrgFragment[] => {
    const statusByOrgId = new Map(
      (statusData?.orgs ?? []).map(org => [org.id, org.latestAxleOrgSyncJob] as const)
    );
    return orgs.map(org => {
      if (!statusByOrgId.has(org.id)) {
        return org;
      }
      return {
        ...org,
        latestAxleOrgSyncJob: statusByOrgId.get(org.id) ?? null,
      };
    });
  }, [orgs, statusData]);

  useEffect(() => {
    if (!pollStatuses) {
      return;
    }
    // Wait for the first status result before deciding jobs are done.
    if (!statusData) {
      return;
    }
    if (!hasActiveSync(orgsWithFreshStatus)) {
      setPollStatuses(false);
    }
  }, [pollStatuses, orgsWithFreshStatus, statusData]);

  const baseListProps = {
    selectedId: selectedNutritionOrg?.id ?? null,
    searchQuery: searchTerm,
  };

  const baseNutritionOrgProps = {
    ...baseListProps,
    orgs: orgsWithFreshStatus,
    loading,
  };

  const handleNutritionOrgLinkClick = (org: AxleOrgSyncOrgFragment) => {
    setSelectedNutritionOrg(org);
  };

  const syncNutritionOrg = async (org: AxleOrgSyncOrgFragment) => {
    if (!org.axleOrgDocumentId) {
      console.error("Cannot sync org - no axleOrgDocumentId");
      return;
    }

    try {
      await createAxleOrgSyncJob({
        variables: {
          input: {
            axleOrgDocumentId: org.axleOrgDocumentId,
          },
        },
      });
      setPollStatuses(true);
      await refetch();
    } catch (error) {
      console.error("Failed to create sync job:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%", overflow: "hidden", flex: 1 }}>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", alignItems: "flex-end" }}>
        <TextField sx={{ width: 350 }} label="Filter Orgs" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <ListsColumn>
          <OrgsListUnlinked {...baseNutritionOrgProps} onLink={handleNutritionOrgLinkClick} />
        </ListsColumn>
        <ListsColumn>
          <OrgsListSynced {...baseNutritionOrgProps} onSync={syncNutritionOrg} />
        </ListsColumn>
      </Box>
    </Box>
  );
};
